// @ts-nocheck
import {
  FOIL_CHARGE_BREAKDOWN_LABEL,
  FOIL_COLOUR_OPTION_TYPES,
  FOIL_SURCHARGE_RESOLVER,
  MESSAGE_TYPE_ENUM,
  OPTION_TYPE_ANGLED_CILL_HORN,
  OPTION_TYPE_INSIDE_SPRAY_COLOUR,
  OPTION_TYPE_OUTSIDE_SPRAY_COLOUR,
  OPTION_TYPE_SPRAY,
  OPTION_TYPE_SPRAY_SIDE,
  OPTION_TYPE_VARIATION,
  OPTION_TYPE_WINDOW_TYPE,
} from "../constants";
import { IAssistantMessage, IPriceBreakdownList, ISurgeItem, IQuestionOption } from "../types";
import { isProfileColourFoilOption } from "./profileColour";

/**
 * Maps each addon size question optionType to its corresponding quantity question optionType.
 * When a size is selected (e.g. Cill Size 25m) and the user then enters a quantity (e.g. 3),
 * the per-metre price already baked into the size option is multiplied by that quantity so
 * the breakdown reflects the total cost for all pieces ordered.
 */
const ADDON_SIZE_TO_QTY_OPTTYPE: Record<number, number> = {
  50: 54, // Head Size → Head Quantity
  51: 55, // Left Jamb Size → Left Jamb Quantity
  52: 56, // Right Jamb Size → Right Jamb Quantity
  53: 57, // Cill Size → Cill Quantity
};

/**
 * Scan user messages and return a map of size-question optionType → entered quantity.
 * If the quantity form has not been answered yet, the size type is absent from the map.
 */
export function buildAddonQuantityMap(
  userMessages: IAssistantMessage[],
): Record<number, number> {
  const qtyTypeToSizeType: Record<number, number> = {};
  Object.entries(ADDON_SIZE_TO_QTY_OPTTYPE).forEach(([sizeType, qtyType]) => {
    qtyTypeToSizeType[qtyType] = Number(sizeType);
  });

  const map: Record<number, number> = {};
  userMessages.forEach((msg) => {
    const sizeType = qtyTypeToSizeType[msg.question?.optionType];
    if (sizeType === undefined) return;
    const form = msg.response?.form ?? {};
    for (const val of Object.values(form)) {
      const num = Number(val);
      if (!isNaN(num) && num > 0) {
        map[sizeType] = num;
        break;
      }
    }
  });
  return map;
}

/**
 * Returns 2 when "Inside and Outside" spray is selected AND the inside colour
 * differs from the outside colour (requires two separate spray runs), otherwise 1.
 */
function resolveSprayColourMultiplier(userMessages: IAssistantMessage[]): number {
  const spraySideMsg = userMessages.find(
    (msg) => msg.question.optionType === OPTION_TYPE_SPRAY_SIDE,
  );
  if (!spraySideMsg) return 1;
  const selectedSide = spraySideMsg.response?.answer?.[0];
  if (selectedSide?.responseValue !== 'inside_and_outside') return 1;

  const insideMsg = userMessages.find(
    (msg) => msg.question.optionType === OPTION_TYPE_INSIDE_SPRAY_COLOUR,
  );
  const outsideMsg = userMessages.find(
    (msg) => msg.question.optionType === OPTION_TYPE_OUTSIDE_SPRAY_COLOUR,
  );
  if (!insideMsg || !outsideMsg) return 1;

  const insideForm = insideMsg.response?.form ?? {};
  const outsideForm = outsideMsg.response?.form ?? {};

  const insideRal = String(insideForm['internal_ral_code'] ?? '').trim().toLowerCase();
  const outsideRal = String(outsideForm['external_ral_code'] ?? '').trim().toLowerCase();
  const insideFb = String(insideForm['internal_farrow_&_ball'] ?? '').trim().toLowerCase();
  const outsideFb = String(outsideForm['external_farrow_&_ball'] ?? '').trim().toLowerCase();

  const insideColour = insideRal || insideFb;
  const outsideColour = outsideRal || outsideFb;

  if (insideColour && outsideColour && insideColour !== outsideColour) return 2;
  return 1;
}

/**
 * Price Calculation Result
 */
export interface CalculatedPrice {
  basePrice: number;
  optionsPrice: number;
  surgeItems: ISurgeItem[];
  totalSurge: number;
  finalPrice: number;
  currency: string;
}

/**
 * Calculate total price from configuration messages
 * Similar to the old quote system's price calculation
 */
export function calculatePriceFromMessages(
  messages: IAssistantMessage[],
  currency: string = "GBP",
): IPriceBreakdownList {
  let priceBreakdown: IPriceBreakdownList = {
    basePrice: 0,
    breakdown: [],
    surchargePrice: 0,
    totalOptionPrice: 0,
    totalPrice: 0,
    flatChargeTotal: 0,
    currency: currency,
  };
  if (!messages || messages.length === 0) {
    return priceBreakdown;
  }
  // Fallback: Calculate from individual message prices
  // Filter only user messages (actual selections)
  const userMessages = messages.filter(
    (msg) => msg.type === MESSAGE_TYPE_ENUM.user,
  );

  // Build answers map (categoryId → selected optionIds) for priceList resolution.
  // Options stored before their priceList conditions were met (e.g. Hardware Items
  // answered before Hardware Colours) will have priceModifier = null.  We re-resolve
  // the priceList here using the full set of answers so those options are priced
  // correctly in the breakdown without requiring a rebake save.
  const allAnswersByCategory: Record<string, string[]> = {};
  userMessages.forEach((msg) => {
    const cat = msg.question?.category;
    if (!cat) return;
    if (!allAnswersByCategory[cat]) allAnswersByCategory[cat] = [];
    msg.response?.answer?.forEach((opt) => {
      if (opt.optionId) allAnswersByCategory[cat].push(opt.optionId);
    });
  });

  // Resolve the frame count for per-frame (optionUnit 5) price scaling.
  // When a priceList-resolved price is used (render-time baking failed),
  // we must manually apply the same per-frame multiplier that applyUnitMultiplier
  // would have applied at render time.
  let perFrameCount = 1;
  for (const msg of userMessages) {
    for (const opt of msg.response?.answer ?? []) {
      if (
        (opt.optionType === OPTION_TYPE_VARIATION ||
          opt.optionType === OPTION_TYPE_WINDOW_TYPE) &&
        (opt.numberOfFrames ?? 0) > 0
      ) {
        perFrameCount = opt.numberOfFrames;
        break;
      }
    }
    if (perFrameCount > 1) break;
  }

  // Build optionId → question-option lookup so we can recover prices that were
  // not baked into response.answer (stored as 0 or null at selection time).
  const questionOptionPriceMap = new Map<string, IQuestionOption>();
  userMessages.forEach((msg) => {
    (msg.question?.options ?? []).forEach((opt) => {
      if (opt.optionId && !questionOptionPriceMap.has(opt.optionId)) {
        questionOptionPriceMap.set(opt.optionId, opt);
      }
    });
  });

  const PER_FRAME_UNIT = 5;

  const resolvePriceListFromConditions = (
    priceList: any[],
    perFrameMultiplier: number,
  ): number => {
    for (const entry of priceList) {
      const allMatch = (entry.conditions ?? []).every((cond: any) => {
        const ids: string[] = allAnswersByCategory[cond.categoryId] ?? [];
        return cond.optionIds?.some((id: string) => ids.includes(id));
      });
      if (allMatch) return (entry.price ?? 0) * perFrameMultiplier;
    }
    return 0;
  };

  const resolvePriceListPrice = (option: IQuestionOption): number => {
    // 1. Use the stored direct price if non-zero — it was correctly baked at
    //    render time (includes per-frame / per-sash multiplier already).
    const direct = option.priceModifier || option.price || 0;
    if (direct !== 0) return direct;

    // 2. Stored price is 0/null — baking failed (e.g. the priceList condition
    //    was not satisfied when the option was rendered).  Re-resolve now using
    //    current answers and apply the per-frame multiplier manually.
    const perFrameMultiplier =
      option.optionUnit === PER_FRAME_UNIT ? perFrameCount : 1;

    if (option.priceList?.length) {
      const resolved = resolvePriceListFromConditions(
        option.priceList,
        perFrameMultiplier,
      );
      if (resolved !== 0) return resolved;
    }

    // 3. Fallback: look up the question option which may have the base price.
    const qOpt = questionOptionPriceMap.get(option.optionId);
    if (qOpt) {
      const qDirect = qOpt.priceModifier ?? qOpt.price ?? 0;
      if (qDirect !== 0) return qDirect;
      if (qOpt.priceList?.length) {
        const resolved = resolvePriceListFromConditions(
          qOpt.priceList,
          perFrameMultiplier,
        );
        if (resolved !== 0) return resolved;
      }
    }
    return 0;
  };

  // Also collect all price-surge messages
  const surgeMessages = messages.filter(
    (msg) => msg.type === MESSAGE_TYPE_ENUM.priceSurge,
  );

  // Collect the optionIds of every "same for all windows" answer the user gave.
  // Unit = numberOfFrames only for questions whose displayCondition.dependsOn
  // directly references one of these IDs — everything else keeps unit = 1.
  const sameForAllOptionIds = new Set<string>();
  userMessages.forEach((msg) => {
    msg.response?.answer?.forEach((opt) => {
      if (opt.responseValue === "same_for_all_windows") {
        sameForAllOptionIds.add(opt.optionId);
      }
    });
  });

  // Resolve numberOfFrames — prefer variation (optionType 64), fall back to window type.
  let sameForAllFrames = 1;
  if (sameForAllOptionIds.size > 0) {
    // Prefer variation (optionType 64) — it is the specific frame count chosen.
    let found = false;
    for (const msg of userMessages) {
      for (const opt of msg.response?.answer ?? []) {
        if (
          opt.optionType === OPTION_TYPE_VARIATION &&
          opt.numberOfFrames > 0
        ) {
          sameForAllFrames = opt.numberOfFrames;
          found = true;
          break;
        }
      }
      if (found) break;
    }
    // Fall back to window-type numberOfFrames when no variation was selected.
    if (!found) {
      for (const msg of userMessages) {
        for (const opt of msg.response?.answer ?? []) {
          if (
            opt.optionType === OPTION_TYPE_WINDOW_TYPE &&
            opt.numberOfFrames > 0
          ) {
            sameForAllFrames = opt.numberOfFrames;
            break;
          }
        }
        if (sameForAllFrames > 1) break;
      }
    }
  }

  /** True when this message's question directly depends on a "same for all"
   *  answer (via displayCondition.dependsOn). Only those questions get the
   *  frame-count multiplier. */
  const dependsOnSameForAll = (msg: IAssistantMessage): boolean =>
    (msg.question.displayCondition?.dependsOn ?? []).some((dep) =>
      dep.optionIds?.some((id) => sameForAllOptionIds.has(id)),
    );

  /** Returns the unit multiplier for an add-on option.
   *  • Priced form fields: multiplier = the value the user entered.
   *  • Questions that directly depend on a "same for all" selection: numberOfFrames.
   *  • Everything else: 1. */
  const getUnitMultiplier = (
    option: IQuestionOption,
    form: Record<string, any>,
    msgDependsOnSameForAll: boolean,
  ): number => {
    // Per-sash (2) and per-metre (3) options have their full multiplier
    // pre-baked into priceModifier/price/vssPrice at render time
    // (see filterQuestionOptions). Keep unit = 1 here to avoid double counting.
    if (
      option.optionUnit === 2 ||
      option.optionUnit === 3 ||
      option.optionUnit === 5
    )
      return 1;

    // Addon size options (Head/Cill/Jamb — optionType 50-53) use optionUnit 5 in
    // the config but are treated as per-metre at render time (resolvePerMeterLength).
    // Their priceModifier is already scaled by the frame dimension, so return 1.
    if (option.optionType >= 50 && option.optionType <= 53) return 1;

    // Per Unit (4): price = priceModifier × entered quantity.
    // Empty / missing = user ordered none → return 0 so the item is excluded
    // from the breakdown. A positive entry (e.g. 4 bay jacks) scales the price.
    if (option.optionUnit === 4) {
      const key = option.responseValue ?? option.optionId;
      const entered = key ? form[key] : undefined;
      if (entered === undefined || entered === null || entered === "") return 0;
      const num = Number(entered);
      return !isNaN(num) && num > 0 ? num : 0;
    }

    const optPrice = option.priceModifier ?? option.price ?? 0;
    if (optPrice > 0 && Object.keys(form).length > 0) {
      // Priced form field — unit = entered value (e.g. number of welds = 2)
      const key = option.responseValue ?? option.optionId;
      const entered = form[key];
      if (entered !== undefined && entered !== "") {
        const num = Number(entered);
        if (!isNaN(num) && num > 0) return num;
      }
    }
    return msgDependsOnSameForAll ? sameForAllFrames : 1;
  };

  const productIWindowTypeItem = userMessages.find(
    (msg) => msg.question.optionType == OPTION_TYPE_WINDOW_TYPE,
  );
  const productIWindowVariationItem = userMessages.find(
    (msg) => msg.question.optionType == OPTION_TYPE_VARIATION,
  );

  // Prefer discountPercentage/discountAmount when present — option.vssDiscountAmount was
  // computed once at config-load time from the option's default vssBase and can go stale
  // relative to option.vssPrice once resolveOptionPriceList later swaps in a more specific
  // priceList entry (which carries its own priceListDiscount %). Recompute fresh from the
  // live vssPrice/discount fields instead of trusting the frozen vssDiscountAmount — same
  // fix already applied to every addon option below, now applied to the base product too.
  const resolveVssDiscountAmount = (option: IQuestionOption): number => {
    const vssBase = option.vssPrice ?? 0;
    if (vssBase < 0) return 0;
    if ((option.discountPercentage ?? 0) > 0) {
      return Math.round(vssBase * (option.discountPercentage! / 100) * 100) / 100;
    }
    if ((option.discountAmount ?? 0) > 0) {
      return option.discountAmount ?? 0;
    }
    return option.vssDiscountAmount ?? 0;
  };

  if (productIWindowVariationItem) {
    const variationOption = productIWindowVariationItem.response.answer[0];
    if (variationOption && variationOption.price) {
      priceBreakdown.basePrice += variationOption.price;
      priceBreakdown.breakdown.push({
        label: variationOption.optionText,
        price: variationOption.price,
        vssPrice: variationOption.vssPrice,
        vssDiscountAmount: resolveVssDiscountAmount(variationOption),
        categoryName: productIWindowVariationItem.question.categoryName,
        optionName: variationOption.optionTypeName,
        unit: 1,
      });
    }
  } else {
    if (productIWindowTypeItem) {
      const baseOption = productIWindowTypeItem.response.answer[0];
      if (baseOption && baseOption.price) {
        priceBreakdown.basePrice = baseOption.price;
        priceBreakdown.breakdown.push({
          label: baseOption.optionText,
          price: baseOption.price,
          vssPrice: baseOption.vssPrice,
          vssDiscountAmount: resolveVssDiscountAmount(baseOption),
          categoryName: productIWindowTypeItem.question.categoryName,
          optionName: baseOption.optionTypeName,
          unit: 1,
        });
      }
    }
  }

  // Pre-compute addon quantity multipliers (Head/Cill/Jamb size × entered quantity).
  const addonQuantityMap = buildAddonQuantityMap(userMessages);

  // When spray=Yes and foil profile colour is selected, the foil colour charges
  // (internal optionType 35, external optionType 34) are included in spray — excluded from breakdown.
  const isFoilProfileTypeSelected = userMessages.some(
    (m) =>
      m.question?.optionType === 60 &&
      m.response?.answer?.some((a: any) =>
        isProfileColourFoilOption(a.responseValue ?? ""),
      ),
  );
  const isSprayYesSelected = userMessages.some(
    (m) =>
      m.question?.optionType === OPTION_TYPE_SPRAY &&
      m.response?.answer?.some((a: any) =>
        (a.responseValue ?? a.optionText ?? "").toLowerCase().includes("yes"),
      ),
  );

  // When "Inside and Outside" spray is selected with different inside/outside colours,
  // two separate spray runs are required so the spray price is doubled.
  const sprayColourMultiplier = resolveSprayColourMultiplier(userMessages);

  const excludedProductItem = userMessages.filter(
    (msg) =>
      ![
        OPTION_TYPE_WINDOW_TYPE,
        OPTION_TYPE_VARIATION,
        OPTION_TYPE_ANGLED_CILL_HORN,
      ].includes(msg.question.optionType),
  );
  excludedProductItem.forEach((msg) => {
    if (msg.response && msg.response.answer) {
      const isInSameForAllCategory = dependsOnSameForAll(msg);
      const form: Record<string, any> = msg.response.form ?? {};
      // Addon quantity multiplier: 1 unless this is a size question paired with a quantity form.
      const addonQty = addonQuantityMap[msg.question?.optionType] ?? 1;
      // Spray side doubles when inside and outside colours differ.
      const spraySideMultiplier =
        msg.question.optionType === OPTION_TYPE_SPRAY_SIDE
          ? sprayColourMultiplier
          : 1;
      // Append "(Frame N)" when the question is a per-frame variant so that
      // duplicate option texts (e.g. two frames with the same Glass Type) are
      // distinguishable in the breakdown.
      const frameMatch = msg.question.responsePath?.match(/\.frame(\d+)$/i);
      const frameSuffix = frameMatch ? ` (Frame ${frameMatch[1]})` : "";

      msg.response.answer.forEach((option) => {
        // Use priceList-resolved price when priceModifier was not baked at render time
        const dealerPrice = resolvePriceListPrice(option);
        if (dealerPrice === 0) return;

        // Spray+Foil rule: when spray=Yes and foil profile selected, internal/external
        // colour charges are included in the spray — skip them entirely from breakdown.
        if (
          isSprayYesSelected &&
          isFoilProfileTypeSelected &&
          FOIL_COLOUR_OPTION_TYPES.has(msg.question.optionType)
        ) {
          return;
        }

        const unit =
          getUnitMultiplier(option, form, isInSameForAllCategory) *
          addonQty *
          spraySideMultiplier;
        const lineTotal = dealerPrice * unit;
        priceBreakdown.totalOptionPrice += lineTotal;

        // Prefer discountPercentage when present — option.vssDiscountAmount was computed
        // at config-load time from the pre-baked per-unit price and may not reflect the
        // unit multiplier that applyUnitMultiplier baked into option.vssPrice later.
        // Use the baked option.vssPrice (correct at answer time) as the base.
        const vssUnitBase = option.vssPrice ?? 0;
        const perUnitVssDiscount =
          vssUnitBase < 0
            ? 0
            : (option.discountPercentage ?? 0) > 0
              ? Math.round(
                  vssUnitBase * (option.discountPercentage! / 100) * 100,
                ) / 100
              : (option.discountAmount ?? 0) > 0
                ? (option.discountAmount ?? 0)
                : (option.vssDiscountAmount ?? 0);

        // Foil colour items (optionType 34/35) when foil profile selected: show as "Foil Charge"
        const isFoilColourItem = isFoilProfileTypeSelected && FOIL_COLOUR_OPTION_TYPES.has(msg.question.optionType);
        priceBreakdown.breakdown.push({
          label: isFoilColourItem ? FOIL_CHARGE_BREAKDOWN_LABEL : option.optionText + frameSuffix,
          price: lineTotal,
          vssPrice: (option.vssPrice ?? 0) * unit,
          vssDiscountAmount: perUnitVssDiscount * unit,
          categoryName: msg.question.categoryName,
          optionName: isFoilColourItem ? '' : option.optionTypeName,
          optionId: option.optionId,
          unit,
        });
      });
    }
  });

  // Collect surge items from all price-surge messages.
  // Only scale by sameForAllFrames when the triggering question's category
  // is a "same for all" category.
  let totalSurgeVssDiscountAmount = 0;
  for (const surgeMessage of surgeMessages) {
    if (surgeMessage.surgeItems && surgeMessage.surgeItems.length > 0) {
      const messageUnit = dependsOnSameForAll(surgeMessage)
        ? sameForAllFrames
        : 1;
      surgeMessage.surgeItems.forEach((item: ISurgeItem) => {
        const surgeUnit = item.applyPerUnit !== undefined ? 1 : messageUnit;
        const isFlat = item.resolverValue === FOIL_SURCHARGE_RESOLVER;

        // vssDiscountAmount is set by createSurgeItem when rule has merged discountPercentage.
        // For older stored surges where it was not computed, derive it from the question's
        // priceConditions (which carry the merged discount after a fresh config load).
        let itemVssDiscount = item.vssDiscountAmount;
        if (itemVssDiscount == null) {
          const matchingRule = (
            surgeMessage.question?.priceConditions as any[]
          )?.find((r: any) => r.dogma_pricingruleid === item.ruleId);
          if (matchingRule) {
            const vssSurge = Math.abs(item.vssSurgeAmount ?? 0);
            if ((matchingRule.discountAmount ?? 0) > 0) {
              itemVssDiscount =
                Math.round(matchingRule.discountAmount * 100) / 100;
            } else if ((matchingRule.discountPercentage ?? 0) > 0) {
              itemVssDiscount =
                Math.round(
                  vssSurge * (matchingRule.discountPercentage / 100) * 100,
                ) / 100;
            }
          }
        }

        const scaledVssDiscount =
          itemVssDiscount != null ? itemVssDiscount * surgeUnit : 0;
        totalSurgeVssDiscountAmount += scaledVssDiscount;

        // surgeAmount is the customer-facing price and already bakes the discount in
        // (see createSurgeItem) — vssPrice/vssDiscountAmount stay on the separate VSS track.
        priceBreakdown.surchargePrice += item.surgeAmount * surgeUnit;
        if (isFlat) {
          priceBreakdown.flatChargeTotal =
            (priceBreakdown.flatChargeTotal ?? 0) +
            item.surgeAmount * surgeUnit;
          priceBreakdown.vssFlatChargeTotal =
            (priceBreakdown.vssFlatChargeTotal ?? 0) +
            (item.vssSurgeAmount ?? 0) * surgeUnit;
        }
        priceBreakdown.breakdown.push({
          label: item.ruleName,
          price: item.surgeAmount * surgeUnit,
          vssPrice: (item.vssSurgeAmount ?? 0) * surgeUnit,
          vssDiscountAmount: scaledVssDiscount || undefined,
          categoryName: "Surcharge",
          optionName: surgeMessage.question.optionTypeName,
          unit: surgeUnit,
          flatCharge: isFlat || undefined,
          resolverValue: item.resolverValue,
        });
      });
    }
  }

  const filteredBreakdown = priceBreakdown.breakdown.filter(
    (breakdownItem) =>
      breakdownItem.vssPrice !== 0 || breakdownItem.price !== 0,
  );

  const vssTotalPrice = filteredBreakdown.reduce(
    (sum, item) => sum + item.vssPrice,
    0,
  );
  const totalVssDiscountAmount = filteredBreakdown.reduce(
    (sum, item) => sum + (item.vssDiscountAmount ?? 0),
    0,
  );

  return {
    ...priceBreakdown,
    breakdown: filteredBreakdown,
    vssTotalPrice,
    totalVssDiscountAmount:
      totalVssDiscountAmount > 0 ? totalVssDiscountAmount : undefined,
    totalSurgeVssDiscountAmount:
      totalSurgeVssDiscountAmount > 0 ? totalSurgeVssDiscountAmount : undefined,
    totalPrice:
      priceBreakdown.basePrice +
      priceBreakdown.totalOptionPrice +
      priceBreakdown.surchargePrice,
  };
}

/**
 * Format price for display
 */
export function formatPrice(price: number, currency: string = "GBP"): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency,
  }).format(price);
}

/**
 * Calculate total price for multiple configurations (cart items)
 */
export function calculateCartTotal(
  cartItems: Array<{ price: { totalPrice: number }; quantity: number }>,
  currency: string = "GBP",
): { total: number; formattedTotal: string } {
  const total = cartItems.reduce(
    (sum, item) => sum + item.price.totalPrice * item.quantity,
    0,
  );

  return {
    total,
    formattedTotal: formatPrice(total, currency),
  };
}
